import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Send, MessageCircle, ChevronDown, Search } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useInView } from "@/hooks/useInView";
import { trackFormSubmission } from "@/lib/gtag";
import { supabase } from "@/integrations/supabase/client";

const countryCodes = [
  { code: "+966", flag: "🇸🇦", name: "السعودية", nameEn: "Saudi Arabia" },
  { code: "+971", flag: "🇦🇪", name: "الإمارات", nameEn: "UAE" },
  { code: "+973", flag: "🇧🇭", name: "البحرين", nameEn: "Bahrain" },
  { code: "+968", flag: "🇴🇲", name: "عُمان", nameEn: "Oman" },
  { code: "+965", flag: "🇰🇼", name: "الكويت", nameEn: "Kuwait" },
  { code: "+974", flag: "🇶🇦", name: "قطر", nameEn: "Qatar" },
  { code: "+20", flag: "🇪🇬", name: "مصر", nameEn: "Egypt" },
  { code: "+962", flag: "🇯🇴", name: "الأردن", nameEn: "Jordan" },
  { code: "+961", flag: "🇱🇧", name: "لبنان", nameEn: "Lebanon" },
  { code: "+964", flag: "🇮🇶", name: "العراق", nameEn: "Iraq" },
  { code: "+212", flag: "🇲🇦", name: "المغرب", nameEn: "Morocco" },
  { code: "+216", flag: "🇹🇳", name: "تونس", nameEn: "Tunisia" },
  { code: "+1", flag: "🇺🇸", name: "أمريكا", nameEn: "USA" },
  { code: "+44", flag: "🇬🇧", name: "بريطانيا", nameEn: "UK" },
  { code: "+91", flag: "🇮🇳", name: "الهند", nameEn: "India" },
  { code: "+92", flag: "🇵🇰", name: "باكستان", nameEn: "Pakistan" },
  { code: "+90", flag: "🇹🇷", name: "تركيا", nameEn: "Turkey" },
];

const ContactSection = ({ defaultProject = "" }: { defaultProject?: string }) => {
  const [form, setForm] = useState({ name: "", phone: "", message: "", project: defaultProject });
  const [submitted, setSubmitted] = useState(false);
  const [selectedCode, setSelectedCode] = useState(countryCodes[0]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeSearch, setCodeSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [headerRef, headerInView] = useInView();
  const [leftRef, leftInView] = useInView();
  const [rightRef, rightInView] = useInView();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCodeOpen(false);
        setCodeSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCodes = countryCodes.filter(
    (c) =>
      c.name.includes(codeSearch) ||
      c.nameEn.toLowerCase().includes(codeSearch.toLowerCase()) ||
      c.code.includes(codeSearch)
  );

  const projectOptions = [
    { value: "malqa", ar: "فلل الملقا", en: "Malqa Villas" },
    { value: "nakhil", ar: "أدوار النخيل", en: "Al Nakhil Floors" },
    { value: "aqiq", ar: "أدوار حي العقيق", en: "Al Aqiq Floors" },
    { value: "adwar-narjis", ar: "أدوار النرجس", en: "Al Narjis Floors" },
    { value: "narjis-villas", ar: "فلل النرجس", en: "Al Narjis Villas" },
    { value: "qairawan", ar: "فلل القيروان", en: "Al Qairawan Villas" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackFormSubmission();
    try {
      const selectedProject = projectOptions.find((p) => p.value === form.project);
      await supabase.from("contact_leads").insert({
        name: form.name.trim().slice(0, 100),
        country_code: selectedCode.code,
        phone: form.phone.trim().slice(0, 20),
        message: form.message.trim().slice(0, 1000),
        project: selectedProject ? selectedProject.ar : null,
        source_page: window.location.pathname,
      });
    } catch (err) {
      console.error("Lead save error:", err);
    }
    setForm({ name: "", phone: "", message: "", project: defaultProject });
    navigate("/thank-you");
  };


  const phoneDropdown = (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => { setCodeOpen(!codeOpen); setCodeSearch(""); }}
        className="flex items-center gap-1 bg-cream/5 border border-cream/10 rounded-lg px-2.5 py-3 text-cream text-sm hover:bg-cream/10 transition-colors min-w-[85px]"
      >
        <span>{selectedCode.flag}</span>
        <span className="font-medium text-xs">{selectedCode.code}</span>
        <ChevronDown size={10} className={`text-cream/40 transition-transform ${codeOpen ? "rotate-180" : ""}`} />
      </button>
      {codeOpen && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-navy border border-cream/15 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
          <div className="p-2 border-b border-cream/10">
            <div className="flex items-center gap-2 bg-cream/5 rounded-lg px-3 py-1.5">
              <Search size={12} className="text-cream/40" />
              <input
                type="text"
                value={codeSearch}
                onChange={(e) => setCodeSearch(e.target.value)}
                placeholder={lang === "ar" ? "ابحث..." : "Search..."}
                className="bg-transparent text-cream text-xs outline-none w-full placeholder:text-cream/50"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto">
            {filteredCodes.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { setSelectedCode(c); setCodeOpen(false); setCodeSearch(""); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-cream/10 transition-colors ${
                  selectedCode.code === c.code ? "bg-cream/10 text-cream" : "text-cream/70"
                }`}
              >
                <span>{c.flag}</span>
                <span className="font-medium text-xs">{c.code}</span>
                <span className="text-[10px] text-cream/40 mr-auto">{lang === "ar" ? c.name : c.nameEn}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );


  const renderContactForm = (mobile = false) => (
    <form
      onSubmit={handleSubmit}
      className={`bg-cream/5 border border-cream/10 ${mobile ? "rounded-2xl p-5 space-y-4" : "rounded-2xl p-8 space-y-5"} flex flex-col`}
    >
      <div>
        <label className="block text-cream text-sm mb-2 font-semibold">{t("contact.formName")}</label>
        <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-cream/5 border border-cream/10 rounded-lg px-3.5 py-3 text-cream placeholder:text-cream/50 focus:border-cream/40 focus:ring-1 focus:ring-cream/20 outline-none transition-all text-sm" placeholder={t("contact.formNamePlaceholder")} />
      </div>
      <div>
        <label className="block text-cream text-sm mb-2 font-semibold">{t("contact.formPhone")}</label>
        <div className="flex gap-2 items-stretch" dir="ltr">
          {phoneDropdown}
          <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="flex-1 min-w-0 bg-cream/5 border border-cream/10 rounded-lg px-3.5 py-3 text-cream placeholder:text-cream/50 focus:border-cream/40 focus:ring-1 focus:ring-cream/20 outline-none transition-all text-sm text-left" placeholder="5xxxxxxxx" dir="ltr" />
        </div>
      </div>
      <div>
        <label className="block text-cream text-sm mb-2 font-semibold">{t("contact.formProject")}</label>
        <div className="relative">
          <select
            required
            value={form.project}
            onChange={(e) => setForm({ ...form, project: e.target.value })}
            className={`w-full appearance-none bg-cream/5 border border-cream/10 rounded-lg px-3.5 py-3 text-cream focus:border-cream/40 focus:ring-1 focus:ring-cream/20 outline-none transition-all text-sm cursor-pointer ${lang === "ar" ? "pl-9" : "pr-9"}`}
          >
            <option value="" disabled className="bg-navy text-cream/50">{t("contact.formProjectPlaceholder")}</option>
            {projectOptions.map((p) => (
              <option key={p.value} value={p.value} className="bg-navy text-cream">
                {lang === "ar" ? p.ar : p.en}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-cream/40 ${lang === "ar" ? "left-3" : "right-3"}`} />
        </div>
      </div>
      <div>
        <label className="block text-cream text-sm mb-2 font-semibold">{t("contact.formMessage")}</label>
        <textarea required rows={mobile ? 3 : 4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`w-full ${mobile ? "min-h-[96px]" : "min-h-[120px]"} bg-cream/5 border border-cream/10 rounded-lg px-3.5 py-3 text-cream placeholder:text-cream/50 focus:border-cream/40 focus:ring-1 focus:ring-cream/20 outline-none transition-all resize-none text-sm`} placeholder={t("contact.formMessagePlaceholder")} />
      </div>
      <button type="submit" className="w-full bg-cream text-navy font-bold py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity mt-1">
        <Send size={16} />
        {submitted ? t("contact.submitted") : t("contact.submit")}
      </button>
    </form>
  );


  return (
    <section id="contact" className="py-10 md:py-28 bg-navy relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cream/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md md:max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`text-center mb-6 md:mb-14 anim-fade-up ${headerInView ? "in-view" : ""}`}>
          <h2 className={`text-2xl md:text-5xl font-black text-cream ${lang === "en" ? "font-houstiq" : ""}`}>{t("contact.title")}</h2>
          <div className="w-10 md:w-16 h-0.5 md:h-1 bg-cream/40 mx-auto mt-2 md:mt-4 rounded-full" />
        </div>

        {/* ===== MOBILE LAYOUT ===== */}
        <div className="md:hidden space-y-3">
          {/* Quick action buttons */}
          <div className="flex gap-3">
            <a href="tel:+966115001577" className="flex-1 flex items-center justify-center gap-2 bg-cream text-navy font-bold py-3 rounded-xl text-sm"><Phone size={16} />{t("contact.phone")}</a>
            <a href="https://wa.me/966115001577" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white font-bold py-3 rounded-xl text-sm"><MessageCircle size={16} />{t("contact.whatsapp")}</a>
          </div>


          {/* Contact form */}
          {renderContactForm(true)}

          {/* Map */}
          <div className="rounded-xl overflow-hidden border border-cream/10">
            <iframe src="https://maps.google.com/maps?q=%D8%A7%D8%AF%D9%88%D8%A7%D8%B1%20%D8%B5%D8%AD%D8%A7%D8%B1%204%D8%8C%208076%20%D8%B1%D9%82%D9%85%20234%D8%8C%20%D8%A7%D9%84%D9%86%D8%B1%D8%AC%D8%B3%D8%8C%20%D8%A7%D9%84%D8%B1%D9%8A%D8%A7%D8%B6%2013324&hl=ar&z=17&output=embed" width="100%" height="180" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sohar Real Estate Location" />
          </div>
        </div>

        {/* ===== DESKTOP LAYOUT ===== */}
        <div className="hidden md:grid lg:grid-cols-5 gap-10">
          <div ref={leftRef} className={`lg:col-span-3 space-y-6 ${lang === "en" ? "anim-fade-left" : "anim-fade-right"} ${leftInView ? "in-view" : ""}`}>
            <p className="text-cream/50 text-base leading-relaxed">{t("contact.desc")}</p>
            <div className="rounded-2xl overflow-hidden border border-cream/10">
              <iframe src="https://maps.google.com/maps?q=%D8%A7%D8%AF%D9%88%D8%A7%D8%B1%20%D8%B5%D8%AD%D8%A7%D8%B1%204%D8%8C%208076%20%D8%B1%D9%82%D9%85%20234%D8%8C%20%D8%A7%D9%84%D9%86%D8%B1%D8%AC%D8%B3%D8%8C%20%D8%A7%D9%84%D8%B1%D9%8A%D8%A7%D8%B6%2013324&hl=ar&z=17&output=embed" width="100%" height="200" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sohar Real Estate Location" />
            </div>
            <a href="https://wa.me/966115001577" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-green-600 text-white font-bold px-7 py-3.5 rounded-xl hover:bg-green-700 transition-colors text-sm">
              <MessageCircle size={20} />{t("contact.whatsapp")}
            </a>
          </div>

          <div ref={rightRef} className={`lg:col-span-2 ${lang === "en" ? "anim-fade-right" : "anim-fade-left"} ${rightInView ? "in-view" : ""}`}>
            {renderContactForm()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
